import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, ListItemIcon, ListItemText, makeStyles, Menu, MenuItem, Tooltip } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import CheckIcon from '@material-ui/icons/Check'

// Compact pill representing one active series in the trend chart.
// Owns nothing -- callers pass color (matches the ECharts palette
// slot), label (the column header), and an `onRemove` handler (null
// for the primary series, which is locked).
//
// Per-series cumulative mode lives in the kebab menu. `mode` is the
// user's explicit choice (or null = auto / chart-type default);
// `effectiveMode` is what's actually being displayed, used to render
// the check next to whichever menu item is currently active. Splitting
// the two lets the chip surface "auto" implicitly (no check on either
// item until the user makes a choice) without needing a third
// "Automatic" menu item.

const useStyles = makeStyles(() => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 24,
    padding: '0 4px 0 8px',
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    borderRadius: 12,
    background: 'var(--opal-surface-default, #fff)',
    fontSize: 12,
    color: 'var(--color-neutral-800, #333)',
    maxWidth: 240
  },
  chipPrimary: {
    background: 'var(--theme-color-surface-d1, #f9f9f9)'
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0
  },
  iconBtn: {
    padding: 0,
    width: 18,
    height: 18,
    color: 'var(--color-neutral-500, #888)',
    '&:hover': {
      color: 'var(--color-neutral-900, #222)'
    }
  },
  iconBtnRight: {
    marginRight: -2
  },
  iconSmall: {
    fontSize: 14
  },
  iconRemove: {
    fontSize: 12
  },
  menuItem: {
    fontSize: 13,
    minHeight: 32,
    paddingTop: 4,
    paddingBottom: 4
  },
  menuItemIcon: {
    minWidth: 28
  },
  hint: {
    color: 'var(--color-neutral-500, #888)',
    fontStyle: 'italic',
    fontSize: 11
  },
  // Small "(auto: cumulative)" suffix shown when the user hasn't
  // explicitly picked a mode for this series -- conveys what the
  // chart-type default resolved to without taking a kebab click.
  autoHint: {
    color: 'var(--color-neutral-500, #888)',
    fontStyle: 'italic',
    fontSize: 10,
    marginLeft: 4,
    flexShrink: 0
  }
}))

function autoHintText (effectiveMode) {
  if (effectiveMode === 'cumulative') return 'auto: cumulative'
  if (effectiveMode === 'perPeriod') return 'auto: per period'
  return null
}

export default function SeriesChip ({ color, label, onRemove, isPrimary, hint, mode, effectiveMode, onModeChange }) {
  const classes = useStyles()
  const menuAnchorRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)

  // The chip is interactive even on the primary (which has no remove X)
  // because the kebab is the only way to switch a primary series's
  // cumulative mode. Only render the kebab when a handler is provided
  // so the chip degrades cleanly for any callers not on the multi-series
  // path.
  const hasMenu = typeof onModeChange === 'function'
  const showRemove = onRemove && !isPrimary

  // `mode` is the user's explicit pick (`'cumulative'` / `'perPeriod'` /
  // null/undefined for auto). When auto, neither menu item is checked
  // and we render a small "(auto: cumulative)" suffix on the chip so
  // the user can see what the chart-type default resolved to without
  // opening the kebab.
  const checkedItem = mode === 'cumulative' ? 'cumulative' : (mode === 'perPeriod' ? 'perPeriod' : null)
  const autoText = mode == null ? autoHintText(effectiveMode) : null
  const handlePick = (next) => {
    setMenuOpen(false)
    if (next !== mode) onModeChange(next)
  }

  return (
    <span className={`${classes.chip} ${isPrimary ? classes.chipPrimary : ''}`}>
      <span className={classes.swatch} style={{ background: color || 'currentColor' }} />
      <span className={classes.label} title={label}>{label}</span>
      {autoText && <span className={classes.autoHint}>{autoText}</span>}
      {hint && <span className={classes.hint}>{hint}</span>}
      {hasMenu && (
        <>
          <Tooltip title='Series options' placement='top' enterDelay={400}>
            <IconButton
              ref={menuAnchorRef}
              size='small'
              className={`${classes.iconBtn} ${!showRemove ? classes.iconBtnRight : ''}`}
              aria-label={`${label} series options`}
              aria-haspopup='menu'
              aria-expanded={menuOpen ? 'true' : 'false'}
              onClick={() => setMenuOpen(true)}
            >
              <MoreVertIcon className={classes.iconSmall} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchorRef.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem className={classes.menuItem} onClick={() => handlePick('cumulative')}>
              <ListItemIcon className={classes.menuItemIcon}>
                {checkedItem === 'cumulative' ? <CheckIcon fontSize='small' /> : null}
              </ListItemIcon>
              <ListItemText primary='Show cumulative' />
            </MenuItem>
            <MenuItem className={classes.menuItem} onClick={() => handlePick('perPeriod')}>
              <ListItemIcon className={classes.menuItemIcon}>
                {checkedItem === 'perPeriod' ? <CheckIcon fontSize='small' /> : null}
              </ListItemIcon>
              <ListItemText primary='Show per-period' />
            </MenuItem>
          </Menu>
        </>
      )}
      {showRemove && (
        <Tooltip title='Remove series' placement='top' enterDelay={400}>
          <IconButton
            size='small'
            className={`${classes.iconBtn} ${classes.iconBtnRight}`}
            aria-label={`Remove ${label}`}
            onClick={onRemove}
          >
            <CloseIcon className={classes.iconRemove} />
          </IconButton>
        </Tooltip>
      )}
    </span>
  )
}

SeriesChip.propTypes = {
  color: PropTypes.string,
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func,
  isPrimary: PropTypes.bool,
  hint: PropTypes.string,
  mode: PropTypes.oneOf(['cumulative', 'perPeriod']),
  effectiveMode: PropTypes.oneOf(['cumulative', 'perPeriod']),
  onModeChange: PropTypes.func
}
